import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';

const ProgressPage = () => {
  const { 
    targets, 
    dailyEntries,
    getCurrentWeekStart,
    rewards,
    punishments,
    addReward,
    addPunishment,
    completeReward,
    completePunishment
  } = useData();
  
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeekStart());
  const [weeklyResults, setWeeklyResults] = useState([]);

  // Force refresh when component mounts to ensure fresh data
  useEffect(() => {
    setSelectedWeek(getCurrentWeekStart());
  }, [getCurrentWeekStart]);

  // Calculate progress directly using current dailyEntries to avoid stale closures
  const calculateWeekProgress = (target, weekStart) => {
    let completed = 0;
    const weekDates = [];
    
    // Generate dates for the week
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      weekDates.push(date.toISOString().split('T')[0]);
    }

    // Count completed days based on target type using current dailyEntries
    weekDates.forEach(date => {
      const entry = dailyEntries[date]?.[target.id] || 0;
      
      if (target.type === 'daily' && entry >= target.amount) {
        completed++;
      } else if (target.type === 'weekly_frequency' && entry > 0) {
        completed++;
      } else if (target.type === 'weekly_total') {
        completed += entry;
      }
    });

    // Calculate total based on target type
    let total;
    if (target.type === 'weekly_total') {
      total = target.amount;
    } else if (target.type === 'weekly_frequency') {
      total = target.frequency || 7;
    } else { // daily
      total = 7;
    }

    const percentage = total > 0 ? Math.min((completed / total) * 100, 100) : 0;
    return { completed, total, percentage };
  };

  useEffect(() => {
    // Calculate progress for all targets for the selected week
    const results = targets.map(target => {
      const progress = calculateWeekProgress(target, selectedWeek);
      
      // More precise completion logic based on target type
      let isCompleted = false;
      if (target.type === 'daily') {
        // For daily targets, need to complete all 7 days
        isCompleted = progress.completed >= 7;
      } else if (target.type === 'weekly_frequency') {
        // For frequency targets, need to meet the minimum frequency
        isCompleted = progress.completed >= (target.frequency || 7);
      } else if (target.type === 'weekly_total') {
        // For total targets, need to reach the target amount
        isCompleted = progress.completed >= target.amount;
      }
      
      return {
        target,
        progress,
        isCompleted
      };
    });
    
    setWeeklyResults(results);
  }, [targets, dailyEntries, selectedWeek]);

  const getWeekString = (weekStart) => {
    const start = new Date(weekStart); // Monday
    const end = new Date(weekStart);
    end.setDate(start.getDate() + 6); // Sunday
    
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (Mon-Sun)`;
  };

  const getPreviousWeek = () => {
    const prevWeek = new Date(selectedWeek);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setSelectedWeek(prevWeek);
  };

  const getNextWeek = () => {
    const nextWeek = new Date(selectedWeek);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const currentWeek = getCurrentWeekStart();
    
    if (nextWeek <= currentWeek) {
      setSelectedWeek(nextWeek);
    }
  };

  const processWeeklyRewards = () => {
    console.log('Processing weekly rewards for week:', selectedWeek.toISOString());
    console.log('Weekly results:', weeklyResults.map(r => ({
      name: r.target.name,
      isCompleted: r.isCompleted,
      progress: r.progress,
      hasReward: !!r.target.reward,
      hasPunishment: !!r.target.punishment
    })));
    
    // Double-check completion status by recalculating to avoid stale data
    weeklyResults.forEach(result => {
      // Recalculate progress to ensure accuracy
      const freshProgress = calculateWeekProgress(result.target, selectedWeek);
      
      // Recalculate completion status
      let freshIsCompleted = false;
      if (result.target.type === 'daily') {
        freshIsCompleted = freshProgress.completed >= 7;
      } else if (result.target.type === 'weekly_frequency') {
        freshIsCompleted = freshProgress.completed >= (result.target.frequency || 7);
      } else if (result.target.type === 'weekly_total') {
        freshIsCompleted = freshProgress.completed >= result.target.amount;
      }
      
      console.log(`Fresh calculation for ${result.target.name}:`, {
        originalCompleted: result.isCompleted,
        freshCompleted: freshIsCompleted,
        progress: freshProgress
      });
      
      if (freshIsCompleted && result.target.reward) {
        // Check if reward already exists
        const existingReward = rewards.find(r => 
          r.targetId === result.target.id && 
          r.weekStart === selectedWeek.toISOString()
        );
        
        if (!existingReward) {
          console.log(`Adding reward for completed target: ${result.target.name}`);
          addReward({
            targetId: result.target.id,
            targetName: result.target.name,
            description: result.target.reward,
            weekStart: selectedWeek.toISOString()
          });
        }
      } else if (!freshIsCompleted && result.target.punishment) {
        // Check if punishment already exists
        const existingPunishment = punishments.find(p => 
          p.targetId === result.target.id && 
          p.weekStart === selectedWeek.toISOString()
        );
        
        if (!existingPunishment) {
          console.log(`Adding punishment for incomplete target: ${result.target.name} (fresh completed: ${freshIsCompleted})`);
          addPunishment({
            targetId: result.target.id,
            targetName: result.target.name,
            description: result.target.punishment,
            weekStart: selectedWeek.toISOString()
          });
        } else {
          console.log(`Punishment already exists for: ${result.target.name}`);
        }
      }
    });
  };

  // Auto-process rewards/punishments for completed weeks
  useEffect(() => {
    const currentWeek = getCurrentWeekStart();
    if (selectedWeek < currentWeek && weeklyResults.length > 0) {
      // Only process if we have valid weekly results
      console.log('Auto-processing rewards/punishments for completed week');
      processWeeklyRewards();
    }
  }, [weeklyResults, selectedWeek]);

  const getOverallProgress = () => {
    if (weeklyResults.length === 0) return { completed: 0, total: 0, percentage: 0 };
    
    const completed = weeklyResults.filter(r => r.isCompleted).length;
    const total = weeklyResults.length;
    const percentage = (completed / total) * 100;
    
    return { completed, total, percentage };
  };

  const overall = getOverallProgress();
  const isCurrentWeek = selectedWeek.getTime() === getCurrentWeekStart().getTime();
  const canGoNext = selectedWeek < getCurrentWeekStart();

  return (
    <div>
      {/* Week Navigation */}
      <div className="card">
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <button 
              className="btn btn-secondary"
              onClick={getPreviousWeek}
              style={{ minWidth: '120px', fontSize: '0.9rem' }}
            >
              ← Previous
            </button>
            
            <button 
              className="btn btn-secondary"
              onClick={getNextWeek}
              disabled={!canGoNext}
              style={{ opacity: canGoNext ? 1 : 0.5, minWidth: '120px', fontSize: '0.9rem' }}
            >
              Next →
            </button>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: 0, color: '#2d3748', fontSize: '1.1rem', lineHeight: '1.3' }}>
              {getWeekString(selectedWeek)}
            </h2>
            {isCurrentWeek && (
              <span className="badge badge-warning" style={{ marginTop: '0.5rem' }}>Current Week</span>
            )}
          </div>
        </div>

        {/* Overall Progress */}
        <div style={{ textAlign: 'center', padding: '1rem', background: '#f7fafc', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2d3748' }}>Week Overview</h3>
          <div className="progress-bar" style={{ height: '12px', marginBottom: '1rem' }}>
            <div 
              className="progress-fill"
              style={{ width: `${overall.percentage}%` }}
            ></div>
          </div>
          <p style={{ fontSize: '1.1rem', fontWeight: '500', color: '#4a5568' }}>
            {overall.completed} of {overall.total} targets completed ({Math.round(overall.percentage)}%)
          </p>
        </div>
      </div>

      {/* Individual Target Progress */}
      {weeklyResults.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>No targets to track</h3>
            <p>Configure some targets first to see your progress!</p>
          </div>
        </div>
      ) : (
        <div className="grid">
          {weeklyResults.map(({ target, progress, isCompleted }) => (
            <div key={target.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ marginBottom: '0.25rem', color: '#2d3748' }}>{target.name}</h3>
                  <p style={{ color: '#718096', fontSize: '0.9rem' }}>
                    {target.type === 'daily' && `${target.amount} ${target.unit} every day`}
                    {target.type === 'weekly_frequency' && `${target.amount} ${target.unit} on ${target.frequency} days`}
                    {target.type === 'weekly_total' && `${target.amount} ${target.unit} total`}
                  </p>
                </div>
                {isCompleted ? (
                  <span className="badge badge-success">✓ Completed</span>
                ) : (
                  <span className="badge badge-warning">In Progress</span>
                )}
              </div>

              <div className="progress-bar" style={{ marginBottom: '0.5rem' }}>
                <div 
                  className="progress-fill"
                  style={{ width: `${progress.percentage}%` }}
                ></div>
              </div>
              
              <p style={{ fontSize: '0.875rem', color: '#4a5568' }}>
                {target.type === 'weekly_total' 
                  ? `${progress.completed} / ${progress.total} ${target.unit}`
                  : `${progress.completed} / ${progress.total} days`
                } ({Math.round(progress.percentage)}%)
              </p>

              {/* Show rewards/punishments */}
              {isCompleted && target.reward && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#c6f6d5', borderRadius: '6px' }}>
                  <p style={{ fontSize: '0.875rem', color: '#22543d', margin: 0 }}>
                    🎁 <strong>Reward earned:</strong> {target.reward}
                  </p>
                </div>
              )}
              
              {!isCompleted && target.punishment && !isCurrentWeek && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fed7d7', borderRadius: '6px' }}>
                  <p style={{ fontSize: '0.875rem', color: '#742a2a', margin: 0 }}>
                    ⚠️ <strong>Punishment due:</strong> {target.punishment}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pending Rewards & Punishments */}
      {(rewards.length > 0 || punishments.length > 0) && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <h3 className="card-title">Pending Rewards & Punishments</h3>
          
          {rewards.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ color: '#38a169', marginBottom: '1rem' }}>🎁 Rewards to Claim</h4>
              {rewards.map(reward => (
                <div key={reward.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#c6f6d5',
                  borderRadius: '8px',
                  marginBottom: '0.5rem'
                }}>
                  <div>
                    <p style={{ fontWeight: '500', color: '#22543d', margin: 0 }}>
                      {reward.description}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#2f855a', margin: 0 }}>
                      From: {reward.targetName}
                    </p>
                  </div>
                  <button 
                    className="btn btn-success btn-small"
                    onClick={() => completeReward(reward.id)}
                  >
                    Claim ✓
                  </button>
                </div>
              ))}
            </div>
          )}

          {punishments.length > 0 && (
            <div>
              <h4 style={{ color: '#e53e3e', marginBottom: '1rem' }}>⚠️ Punishments to Complete</h4>
              {punishments.map(punishment => (
                <div key={punishment.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#fed7d7',
                  borderRadius: '8px',
                  marginBottom: '0.5rem'
                }}>
                  <div>
                    <p style={{ fontWeight: '500', color: '#742a2a', margin: 0 }}>
                      {punishment.description}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#c53030', margin: 0 }}>
                      From: {punishment.targetName}
                    </p>
                  </div>
                  <button 
                    className="btn btn-danger btn-small"
                    onClick={() => completePunishment(punishment.id)}
                  >
                    Done ✓
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgressPage;
